<template>
  <v-container
    class="bg-white"
    width="60%">
    <v-card
      class="pa-5"
      variant="text">
      <v-card-item>
        <v-card-title>Zusammenfassung</v-card-title>
        <template #append>
          <v-fab
            elevation="0"
            icon="mdi-close"
            @click.prevent="$emit('close')"></v-fab>
        </template>
      </v-card-item>

      <v-card-text>
        <p>
          {{
            $t(
              "views.licence.review." +
                (reviewData.yearly ? "yearlyCosts" : "monthlyCosts"),
              { costs: reviewData.newCosts }
            )
          }}
        </p>
        <p
          v-if="reviewData.moduleTrial"
          data-cy="licence-popup-module-trial">
          {{
            $t("views.licence.promo.popupModuleTrial", {
              modules: formatModuleSlots(t, reviewData.moduleTrial.modules),
              days: reviewData.moduleTrial.days,
              interpolation: { escapeValue: false }
            })
          }}
        </p>
        <p
          v-if="reviewData.previewLoading"
          data-cy="licence-popup-loading">
          <v-progress-circular
            :size="15"
            indeterminate />
          {{ $t("views.licence.review.previewLoading") }}
        </p>
        <p
          v-if="reviewData.previewFailed"
          class="text-error"
          data-cy="licence-popup-preview-failed">
          {{
            $t(
              "views.licence.review." +
                (reviewData.saveBlocked ? "previewFailedRetry" : "previewFailed")
            )
          }}
        </p>
        <p
          v-if="discountText"
          class="text-success"
          data-cy="licence-popup-discount">
          {{ discountText }}
        </p>
        <p
          v-if="reviewData.keepMonthly"
          data-cy="licence-popup-keep-monthly">
          <template v-if="reviewData.pendingSwitch">
            {{ $t("views.licence.review.pendingSwitchKeepMonthly", {date: dateLabel}) }}
          </template>
          <template v-else>
            {{ $t("views.licence.review.switchYearModuleRemove", {date: dateLabel}) }}
          </template>
        </p>
        <p
          v-if="reviewData.revert"
          data-cy="licence-popup-switch-revert">
          {{ $t("views.licence.review.switchRevert") }}
        </p>
        <p
          v-else-if="reviewData.switchMonthly"
          data-cy="licence-popup-switch-monthly">
          {{ $t("views.licence.review.switchMonthlyInfo", {date: dateLabel}) }}
        </p>
        <p
          v-if="reviewData.trialInfo"
          data-cy="licence-popup-trial">
          {{
            $t("views.licence.promo.trialToday", {
              date: new Date(reviewData.trialInfo.date * 1000).toLocaleDateString(),
              amount: reviewData.trialInfo.amount,
              interpolation: { escapeValue: false }
            })
          }}
        </p>
        <p
          v-if="reviewData.trialInfo?.create"
          data-cy="licence-popup-trial-card-hint">
          {{
            $t("views.licence.promo.trialCardHint", {
              date: new Date(reviewData.trialInfo.date * 1000).toLocaleDateString(),
              interpolation: { escapeValue: false }
            })
          }}
        </p>
        <p
          v-if="
            chargeNeeded > 0 &&
            !reviewData.trialInfo &&
            (reviewData.additionalPrice || reviewData.previewLoading)
          "
          data-cy="licence-popup-additional">
            <template v-if="reviewData.nothingDue">
              {{ $t("views.licence.review.nothingDueNow") }}
            </template>
            <template v-else>
            <template v-if="reviewData.switchYearly && !reviewData.keepMonthly">
              {{ $t("views.licence.review.additionalCostsSwitchYear") }}
            </template>
            <template v-else>
              {{
                $t(
                  "views.licence.review.additionalCosts" +
                    (reviewData.currentMode === "yearly" ? "Year" : ""),
                  {
                    remainingDays: reviewData.remainingDays
                  }
                )
              }}
            </template>
            <template v-if="reviewData.additionalPrice">
              <span data-cy="licence-popup-additional-price">{{ reviewData.additionalPrice }}</span>
            </template>
            <v-progress-circular
              :size="15"
              v-else-if="reviewData.previewLoading"
              indeterminate />
            </template>
        </p>

        <v-data-table
          :headers="headers"
          :items="items"
          hide-default-footer></v-data-table>

        <p v-if="removed" data-cy="licence-popup-removed">
          <template v-if="private">
            {{
              $t("views.licence.review.module-removed", {
                date: (new Date(reviewData.date * 1000)).toLocaleDateString()
              })
            }}
          </template>
          <template v-else>
            {{ $t("views.licence.review.module-removed-organization") }}
          </template>
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          elevation="3"
          data-cy="licence-popup-cancel"
          @click="emits('close')">
          Abbrechen
        </v-btn>
        <v-btn
          elevation="3"
          data-cy="licence-popup-save"
          :disabled="reviewData.previewLoading || reviewData.saveBlocked"
          @click="emits('save')"
          >Speichern</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script lang="ts" setup>
import { listModule } from "@/types/global";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { formatModuleSlots } from "@/views/Main/Licence/moduleComposable";

const { t } = useTranslation();
const emits = defineEmits(["close", "save"]);

const props = defineProps({
  private: {
    type: Boolean,
    required: false
  },
  reviewData: {
    type: Object,
    required: true
  },
  modulesRemoved: {
    type: Object,
    required: true
  },
  initialItems: {
    type: Array,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  chargeNeeded: {
    type: Number,
    required: true
  }
});

const headers = [
  { title: "Modul", key: "module" },
  { title: "Alte Anzahl", key: "oldQuantity" },
  { title: "Neue Anzahl", key: "newQuantity" }
];

const items = computed(() => {
  return props.initialItems.map((item: listModule, index) => {
    return {
      module: t("views.licence.modules." + item.name + ".name"),
      oldQuantity: item.slots,
      newQuantity: props.items[index]?.slots
    };
  });
});

const dateLabel = computed(() =>
  new Date(props.reviewData.date * 1000).toLocaleDateString()
);

// Information about the discount: "Jetzt {amount}, danach ..." only when something is charged now
const discountText = computed(() => {
  const d = props.reviewData.discount;
  if (!d) return "";
  const showNow =
    props.chargeNeeded > 0 &&
    !!props.reviewData.additionalPrice &&
    !props.reviewData.nothingDue &&
    !props.reviewData.trialInfo;
  const params = {
    code: d.code,
    now: props.reviewData.additionalPrice,
    discounted: d.discountedCosts,
    list: d.listCosts,
    unit: t(
      "views.licence.promo.popup.unit." +
        (props.reviewData.yearly ? "yearly" : "monthly")
    ),
    date: d.end ? new Date(d.end * 1000).toLocaleDateString() : "",
    interpolation: { escapeValue: false }
  };
  let key: string = d.duration;
  if (key === "once") key = showNow ? "onceNow" : "once";
  else if (key === "repeating") {
    key = (showNow ? "repeatingNow" : "repeating") + (d.end ? "" : "Remaining");
  }
  return t("views.licence.promo.popup." + key, params);
});

const removed = computed(() => {
  return Object.values(props.modulesRemoved).filter((item) => item > 0).length;
});
</script>
